import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MaskLines } from "./motion";
import { useSmoothScroll } from "@/lib/smooth-scroll";

const HERO_IMG =
  "https://customer-assets-lxgj4vgw.emergentagent.net/job_montebello-lodge/artifacts/mgwczhop_WhatsApp%20Image%202026-07-27%20at%2011.25.11%20%282%29.jpeg";

const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const { scrollTo } = useSmoothScroll();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  // Parallax: image drifts down + scales, content rises and fades.
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      ref={ref}
      className="relative min-h-screen w-full flex items-end pb-24 pt-32 overflow-hidden"
    >
      <motion.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
        <img
          src={HERO_IMG}
          alt="Copa de árvore centenária na floresta da Casa Montebello"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-black/25" />
      </motion.div>

      <motion.div
        className="relative z-10 mx-auto max-w-[1400px] px-6 lg:px-12 w-full"
        style={{ y: contentY, opacity: contentOpacity }}
      >
        <div className="max-w-5xl">
          <motion.div
            className="flex items-center gap-4 mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.1 }}
          >
            <motion.span
              className="h-px bg-cream/70"
              initial={{ width: 0 }}
              animate={{ width: 56 }}
              transition={{ duration: 1, ease: EASE, delay: 0.2 }}
            />
            <p className="overline text-cream/85">
              Alojamento Local · Felgueiras, Porto
            </p>
          </motion.div>

          <h1
            data-testid="hero-title"
            className="font-display text-cream text-[3.2rem] sm:text-6xl lg:text-[7rem] leading-[0.92] tracking-tight font-light"
          >
            <MaskLines
              lines={["Refúgio na floresta,"]}
              delay={0.35}
            />
            <MaskLines
              lines={[<span key="l2" className="italic">abraçado pela natureza.</span>]}
              delay={0.5}
            />
          </h1>

          <motion.p
            className="mt-8 max-w-2xl text-cream/85 text-lg sm:text-xl leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.8 }}
          >
            Uma casa privada, escondida entre árvores centenárias, com cozinha
            equipada e amplos espaços comuns. Feita para famílias e grupos que
            procuram tempo — muito tempo — em boa companhia.
          </motion.p>

          <motion.div
            className="mt-12 flex flex-wrap items-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.95 }}
          >
            <button
              data-testid="hero-reservar-btn"
              onClick={() => scrollTo("#reservar")}
              className="group inline-flex items-center gap-3 h-14 pl-8 pr-6 rounded-full bg-cream text-forest font-medium text-[0.82rem] tracking-[0.2em] uppercase hover:bg-white transition-all"
            >
              Reservar Agora
              <span className="flex items-center justify-center w-9 h-9 rounded-full bg-forest text-cream group-hover:translate-x-1 transition-transform">
                <ArrowRight size={16} strokeWidth={1.75} />
              </span>
            </button>
            <button
              data-testid="hero-descobrir-btn"
              onClick={() => scrollTo("#sobre")}
              className="inline-flex items-center h-14 px-8 rounded-full border border-cream/50 text-cream text-[0.82rem] tracking-[0.2em] uppercase font-medium hover:bg-cream/10 transition-colors"
            >
              Descobrir a Casa
            </button>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
      >
        <motion.div
          className="w-[1px] h-14 bg-cream/50"
          animate={{ scaleY: [0.3, 1, 0.3], transformOrigin: ["top", "top", "top"] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>
    </section>
  );
}
